import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useEffect, useMemo, useState } from "react";
import {
  TreeNode,
  createNodesForSteps,
  createNodesForStepsGroupedByState,
} from "~/components/build/Show/components/Sidebar/TreeNode";
import { CollapsibleState } from "~/components/build/Show/components/Sidebar/TreeItem/CollapsibleState";
import {
  DisplayMode,
  useStateCounts,
  getRelevantSubFiltersForStep,
  filterJobs,
  FilterByOption,
  filterSteps,
} from "~/components/build/Show/components/Sidebar/useFilterStore";
import { CommandJob } from "~/components/build/Show/lib/types";
import useExpandedIds from "~/components/build/Show/components/Sidebar/useExpandedIds";
import { topLevelStepsQueryKey, useSteps } from "~/components/build/Show/lib/useSteps";
import { useQueryClient } from "@tanstack/react-query";
import { fetchBuildSteps } from "~/components/build/Show/lib/queries";
import { Step } from "~/lib/pipeline";

export interface FlatTreeNode extends TreeNode {
  depth: number;
  collapsibleState: CollapsibleState;
  parentPath: FlatTreeNode[];

  // Index of item in flattened tree.
  index: number;
}

/**
 * Returns a list of visible nodes based on expanded states and depth.
 */
export function flattenTreeNodes(
  nodes: TreeNode[],
  collapsibleStates: Map<string, CollapsibleState>,
  depth: number,
  filters: FilterByOption[] = [],
  index = 0,
): FlatTreeNode[] {
  const result: FlatTreeNode[] = [];

  for (const node of nodes) {
    const visibleNode = {
      ...node,
      collapsibleState:
        node.childNodes === undefined
          ? CollapsibleState.None
          : collapsibleStates.get(node.id) || CollapsibleState.Collapsed,
      depth,
      parentPath: [],
      index,
    };

    result.push(visibleNode);
    index++;

    if (node.childNodes) {
      const childNodes = node.childNodes(visibleNode.collapsibleState, filters);
      const children = flattenTreeNodes(childNodes, collapsibleStates, depth + 1, filters, index);

      children.forEach((child) => {
        child.parentPath = [visibleNode, ...child.parentPath];
        child.index = index++;
      });

      result.push(...children);
    }
  }

  return result;
}

export function filterStepJobs<T extends CommandJob>(jobs: T[], step, build): T[] {
  const filters = getRelevantSubFiltersForStep(step, build);
  return filterJobs(jobs, filters, build);
}

interface UseVisibleNodesProps {
  /**
   * Display mode to be used
   */
  displayMode?: DisplayMode;

  /**
   * Filters to apply to the list of steps.
   */
  filters?: FilterByOption[];
}

/**
 * Perform query (if necessary) for the top-level nodes of the tree.
 */
const useTreeNodesQuery = (displayMode: DisplayMode = DisplayMode.State) => {
  const { build } = useBuild();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);

  const query = useMemo(() => {
    // We've got all the data so return.
    if (!Features.ProgressiveBuildShow) {
      return Promise.resolve();
    }

    if (displayMode === DisplayMode.Pipeline) {
      return queryClient.fetchQuery({
        queryKey: topLevelStepsQueryKey(build),
        queryFn: () =>
          fetchBuildSteps({
            buildDataBasePath: build.buildDataBasePath,
            filters: { depth: "top" },
          }),
      });
    } else if (displayMode === DisplayMode.State) {
      return Promise.resolve();
    }
    throw new Error(`Invalid display mode: ${displayMode}`);
  }, [displayMode]);

  useEffect(() => {
    query.finally(() => {
      setIsLoading(false);
    });
  }, [query]);

  return {
    // Display by state isn't blocked on an initial request so we don't need to wait for it to load.
    isLoading: displayMode === DisplayMode.State ? false : isLoading,
  };
};

/**
 * Based on a filtered list of steps and display mode, return a list of visible tree nodes
 * respecting the expanded state of steps or state groups.
 */
export function useTreeNodes({ displayMode, filters }: UseVisibleNodesProps = {}): {
  isLoading: boolean;
  nodes: FlatTreeNode[];
} {
  const collapsibleStates = useExpandedIds();
  const stateCounts = useStateCounts();
  const { steps, stepJobs } = useSteps();
  const { build } = useBuild();

  /**
   * Fetch the initial tree nodes based on the display mode and filters, which in turn updates the steps store.
   */
  const { isLoading } = useTreeNodesQuery(displayMode);

  /**
   * Transform steps from the store into tree nodes based on the display mode.
   */
  const nodes = useMemo(() => {
    if (displayMode === DisplayMode.Pipeline) {
      let filteredSteps: Step[];

      if (!Features.ProgressiveBuildShow) {
        // Apply filters to the top-level steps. At a bare minimum, this removes ignored steps and collapses consecutive wait steps.
        filteredSteps = filterSteps(steps, filters || [], build);
      } else {
        // Ignored steps are pre-filtered out so we only need to filter here when filters are present.
        filteredSteps = filters?.length ? filterSteps(steps, filters, build) : steps;
      }

      return createNodesForSteps(build, filteredSteps);
    } else if (displayMode === DisplayMode.State) {
      return createNodesForStepsGroupedByState(build, stateCounts);
    }
    throw new Error(`Invalid display mode: ${displayMode}`);
  }, [steps, displayMode, filters]);

  /**
   * Flatten the tree nodes into a flat array for virtualization.
   */
  const visible: FlatTreeNode[] = useMemo(() => {
    return flattenTreeNodes(nodes, collapsibleStates, 0, filters);
  }, [nodes, collapsibleStates, filters, steps, stepJobs]);

  return { isLoading, nodes: visible };
}
